<script setup>
import {computed, getCurrentInstance, ref} from 'vue'
import {renderMarkdown, renderData} from '@/lib/chat-markdown'

const props = defineProps({
  entry: {type: Object, required: true},
  disabled: {type: Boolean, default: false}
})

const emit = defineEmits(['cancel-notice', 'continue', 'answer', 'favorite'])

// tap (or click) twice on your own message: save it as a favorite prompt
let lastTap = 0

function onUserTap() {
  const now = Date.now()
  if (now - lastTap < 400 && (props.entry.text || '').trim()) {
    lastTap = 0
    if (window.getSelection) window.getSelection().removeAllRanges()
    emit('favorite', props.entry.text)
    return
  }
  lastTap = now
}

// a pick-list question: pick-one answers on the click, pick-many on Send
const sel = ref([])
const canPick = computed(() => props.entry.state === 'open' && !props.disabled)

function isPicked(o) {
  return props.entry.state === 'open' ? sel.value.includes(o) : (props.entry.picked || []).includes(o)
}

function pick(o) {
  if (!canPick.value) return
  if (!props.entry.multiple) return emit('answer', [o])
  sel.value = sel.value.includes(o) ? sel.value.filter(x => x !== o) : [...sel.value, o]
}

function sendPicked() {
  if (canPick.value && sel.value.length) emit('answer', props.entry.options.filter(o => sel.value.includes(o)))
}

const open = ref(false)
const {proxy} = getCurrentInstance()

// Delegate clicks on GLI links (rendered into v-html) to the global lookup.
function onGliClick(e) {
  const a = e.target.closest && e.target.closest('a.gli-link')
  if (!a) return
  e.preventDefault()
  const gli = a.getAttribute('data-gli')
  if (gli) proxy.global_lookup(gli, true)
}

const CHANNEL_NAMES = {inapp: 'the bell', push: 'your phone'}
const notice = computed(() => {
  const r = props.entry.rule
  if (!r) return null

  return {
    title: r.label || r.trigger,
    about: r.about,
    only: r.only_when,
    repeats: r.repeats,
    via: (r.channels || []).map(c => CHANNEL_NAMES[c] || c).join(' and '),
  }
})

// a file is kept for a week; past that the link is gone
const fileExpired = computed(() => !!props.entry.expires && new Date(props.entry.expires) < new Date())
const fileUntil = computed(() => props.entry.expires
  ? new Date(props.entry.expires).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})
  : '')

const bodyHtml = computed(() => renderMarkdown(props.entry.text || ''))
const dataHtml = computed(() => renderData(props.entry.data))
const argsText = computed(() => {
  try {
    return JSON.stringify(props.entry.args || {})
  } catch {
    return ''
  }
})
</script>

<template>
  <!-- user -->
  <div v-if="entry.kind === 'user'" class="align-self-end chat-user" title="Double-click to save as a favorite prompt" @click="onUserTap">
    <!-- files sent with the question: open in a new tab -->
    <div v-if="entry.files && entry.files.length" class="d-flex flex-wrap" :class="{'mb-1': entry.text}">
      <a v-for="f in entry.files" :key="f.id" :href="f.url" target="_blank" rel="noopener" class="chat-sent-file">
        <img v-if="f.kind === 'image'" :src="f.url" :alt="f.name" class="chat-sent-thumb">
        <template v-else>
          <font-awesome-icon :icon="['fas', {pdf: 'file-pdf', email: 'envelope'}[f.kind] || 'file-excel']" class="mr-1"
                             :class="{pdf: 'text-danger', email: 'text-primary'}[f.kind] || 'text-success'"/>{{ f.name }}
        </template>
      </a>
    </div>
    <span class="chat-prewrap">{{ entry.text }}</span>
  </div>

  <!-- assistant -->
  <div v-else-if="entry.kind === 'bot'" class="align-self-stretch chat-md" v-html="bodyHtml" @click="onGliClick"/>

  <!-- error -->
  <div v-else-if="entry.kind === 'error'" class="align-self-stretch chat-error">
    &#9888; {{ entry.text }}
  </div>

  <!-- stopped at the tool-call limit: offer to carry on -->
  <div v-else-if="entry.kind === 'limit'" class="align-self-stretch chat-limit">
    <div>
      I used up my tool calls for one reply ({{ entry.calls }} so far) before I finished.
      <template v-if="!entry.canContinue">I've carried on a few times already - try a narrower question.</template>
    </div>
    <b-button v-if="entry.canContinue" size="sm" variant="outline-primary" pill class="mt-2 px-3" @click="$emit('continue')">
      Keep going
    </b-button>
  </div>

  <!-- the assistant asks the person to choose -->
  <div v-else-if="entry.kind === 'choice'" class="align-self-stretch chat-choice"
       :class="{'chat-choice-off': entry.state === 'closed'}">
    <div class="font-weight-bold mb-2">{{ entry.question }}</div>
    <div class="d-flex flex-wrap chat-choice-options">
      <button v-for="o in entry.options" :key="o" type="button" class="chat-option"
              :class="{'chat-option-on': isPicked(o)}" :disabled="!canPick && !isPicked(o)" @click="pick(o)">
        <font-awesome-icon v-if="entry.multiple" class="mr-2"
                           :icon="isPicked(o) ? 'fa-solid fa-square-check' : 'fa-regular fa-square'"/>
        {{ o }}
      </button>
    </div>
    <div v-if="entry.multiple && entry.state === 'open'" class="mt-2 d-flex align-items-center">
      <b-button size="sm" variant="primary" pill class="px-3" :disabled="!canPick || !sel.length" @click="sendPicked">Send</b-button>
      <span class="small text-muted ml-2">Pick any that apply, or type your own answer below.</span>
    </div>
    <div v-else-if="entry.state === 'open'" class="small text-muted mt-2">Or type your own answer below.</div>
  </div>

  <!-- a notification the assistant just set up -->
  <div v-else-if="entry.kind === 'notice' && notice" class="align-self-stretch chat-notice"
       :class="{'chat-notice-off': entry.state === 'cancelled' || entry.state === 'gone'}">
    <font-awesome-icon :icon="['fas', 'bell']" class="chat-notice-icon"/>
    <div class="flex-fill" style="min-width: 0">
      <div class="font-weight-bold">
        {{ entry.state === 'cancelled' ? 'Notification canceled' : entry.created ? 'Notification set' : 'You already had this notification' }}
        <span class="font-weight-normal text-muted"> - {{ notice.title }}</span>
      </div>
      <div class="small">
        <div><span class="text-muted">Watching:</span> {{ notice.about }}</div>
        <div v-if="notice.only"><span class="text-muted">Only when:</span> {{ notice.only }}</div>
        <div><span class="text-muted">Repeats:</span> {{ notice.repeats }} <span class="text-muted">&middot; tells you on</span> {{ notice.via }}</div>
      </div>
    </div>
    <b-button v-if="entry.state === 'active' || entry.state === 'cancelling'" size="sm" variant="outline-secondary"
              class="ml-3 align-self-start" :disabled="entry.state === 'cancelling'" @click="$emit('cancel-notice')">
      Cancel
    </b-button>
    <span v-else-if="entry.state === 'gone'" class="small text-muted ml-3 align-self-start">Already used</span>
  </div>

  <!-- an Excel / PDF file the assistant made (here or on another device) -->
  <div v-else-if="entry.kind === 'file'" class="align-self-stretch chat-notice align-items-center"
       :class="{'chat-notice-off': fileExpired}">
    <font-awesome-icon :icon="['fas', entry.type === 'pdf' ? 'file-pdf' : 'file-excel']" class="chat-notice-icon"
                       :class="entry.type === 'pdf' ? 'text-danger' : 'text-success'" size="lg"/>
    <div class="flex-fill" style="min-width: 0">
      <div class="font-weight-bold">{{ entry.name }}</div>
      <div class="small text-muted">
        <template v-if="entry.sheets.length">{{ entry.sheets.map(s => s.name + ' (' + s.rows + ' rows)').join(', ') }} &middot; </template>{{ entry.type === 'pdf' ? 'PDF document · ' : '' }}<template v-if="fileExpired">expired</template><template v-else-if="fileUntil">available until {{ fileUntil }}</template>
      </div>
    </div>
    <span v-if="fileExpired" class="small text-muted ml-3">Ask again to remake it</span>
    <b-button v-else :href="entry.url" variant="success" size="sm" class="ml-3" download>
      <font-awesome-icon :icon="['fas', 'download']" class="mr-1"/>Download
    </b-button>
  </div>

  <!-- photos the assistant found: the record galleries' own viewer (thumbnails, full screen) -->
  <div v-else-if="entry.kind === 'photos'" class="align-self-stretch chat-photos">
    <photo-gallery readonly :photos="entry.photos" :width="150">
      <template #footer="{photo}">
        <div class="text-truncate"><small>
          <span v-if="photo.similarity" class="text-muted mr-1" title="How alike">{{ photo.similarity }}%</span>
          <b-link v-for="r in (photo.on || [])" :key="r.gli" class="mr-1" :title="r.type + ' ' + r.gli + (photo.summary ? ' - ' + photo.summary : '')"
                  @click.stop="proxy.global_lookup(r.gli, true)">{{ r.gli }}</b-link>
        </small></div>
      </template>
    </photo-gallery>
  </div>

  <!-- tool call -->
  <div v-else class="align-self-stretch chat-tool">
    <div class="d-flex align-items-center text-monospace small text-muted cursor-pointer" @click="open = !open">
      <span class="mr-2">{{ open ? '▾' : '▸' }}</span>
      <span class="font-weight-bold mr-2">{{ entry.name }}</span>
      <span class="text-truncate flex-fill">({{ argsText }})</span>
      <b-spinner v-if="entry.pending" small class="ml-2"/>
      <span v-else class="ml-2" :class="entry.ok ? 'text-success' : 'text-danger'">
        {{ entry.ok ? '✓' : '✗' }} {{ entry.status || '' }} {{ entry.summary }}
      </span>
    </div>
    <b-collapse v-model="open" class="mt-2">
      <div v-if="entry.route" class="text-muted font-italic small text-monospace mb-1">{{ entry.route }}</div>
      <div v-if="!entry.pending" v-html="dataHtml" @click="onGliClick"/>
    </b-collapse>
  </div>
</template>

<style scoped>
.chat-sent-file {
  display: inline-flex;
  align-items: center;
  margin: 0 .35rem .35rem 0;
  padding: .15rem .6rem;
  border-radius: .8rem;
  background: #fff;
  color: #333;
  font-size: .85rem;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.chat-sent-thumb {
  max-width: 160px;
  max-height: 120px;
  margin: -.15rem -.6rem;
  border-radius: .8rem;
  object-fit: cover;
}

.chat-user {
  max-width: 80%;
  padding: .6rem 1rem;
  border-radius: 1.25rem;
  background: #f4f4f4;
  color: #0d0d0d;
  line-height: 1.5;
}

.chat-prewrap {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.chat-error {
  padding: .5rem .85rem;
  border-radius: .75rem;
  background: #fdf0ef;
  color: #b3261e;
  font-size: .9rem;
}

.chat-limit {
  padding: .6rem .85rem;
  border-radius: .75rem;
  background: #fff8e6;
  color: #6b5200;
  font-size: .9rem;
}

.chat-choice {
  padding: .75rem .9rem;
  border: 1px solid #d7e6f7;
  border-radius: .75rem;
  background: #f3f8fe;
}

.chat-choice-off {
  opacity: .55;
  background: #fafafa;
  border-color: #ececec;
}

.chat-choice-options {
  gap: .5rem;
}

.chat-option {
  padding: .35rem .9rem;
  border: 1px solid #b7cfea;
  border-radius: 1.25rem;
  background: #fff;
  color: #0b57d0;
  font-size: .92rem;
  text-align: left;
  cursor: pointer;
}

.chat-option:hover:not(:disabled) {
  background: #e8f1fc;
}

.chat-option-on {
  border-color: #0b57d0;
  background: #0b57d0;
  color: #fff;
}

.chat-option-on:hover:not(:disabled) {
  background: #0b57d0;
}

.chat-option:disabled {
  cursor: default;
}

.chat-option:disabled:not(.chat-option-on) {
  color: #8a8a8a;
  border-color: #e2e2e2;
}

.chat-notice {
  display: flex;
  align-items: flex-start;
  border: 1px solid #d7e6f7;
  border-radius: .75rem;
  padding: .65rem .85rem;
  background: #f3f8fe;
  overflow-wrap: anywhere;
}

.chat-notice-off {
  opacity: .6;
  background: #fafafa;
  border-color: #ececec;
}

.chat-notice-icon {
  margin: .25rem .75rem 0 0;
  color: #2f6fb3;
}

.chat-tool {
  border: 1px solid #ececec;
  border-radius: .75rem;
  padding: .4rem .75rem;
  background: #fafafa;
}

.cursor-pointer {
  cursor: pointer;
}

.chat-md {
  color: #0d0d0d;
  font-size: 1rem;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.chat-md >>> > :first-child {
  margin-top: 0;
}

.chat-md >>> > :last-child {
  margin-bottom: 0;
}

.chat-md >>> p {
  margin: 0 0 .9rem;
}

.chat-md >>> h1,
.chat-md >>> h2,
.chat-md >>> h3,
.chat-md >>> h4 {
  font-weight: 600;
  line-height: 1.35;
  margin: 1.4rem 0 .5rem;
}

.chat-md >>> h1 {
  font-size: 1.4rem;
}

.chat-md >>> h2 {
  font-size: 1.2rem;
}

.chat-md >>> h3,
.chat-md >>> h4 {
  font-size: 1.05rem;
}

.chat-md >>> ul,
.chat-md >>> ol {
  margin: 0 0 .9rem;
  padding-left: 1.5rem;
}

.chat-md >>> li {
  margin-bottom: .25rem;
}

.chat-md >>> code {
  padding: .15rem .4rem;
  border-radius: .35rem;
  background: #f1f1f1;
  color: #0d0d0d;
  font-size: .88em;
}

.chat-md >>> pre {
  margin: 0 0 .9rem;
  padding: .85rem 1rem;
  border-radius: .75rem;
  background: #f7f7f8;
  overflow-x: auto;
}

.chat-md >>> pre code {
  padding: 0;
  background: none;
}

.chat-md >>> table {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  margin: .25rem 0 1rem;
  border: 0;
  border-collapse: collapse;
  font-size: .92rem;
  line-height: 1.5;
}

.chat-md >>> th,
.chat-md >>> td {
  padding: .5rem .85rem;
  border: 0;
  border-bottom: 1px solid #ececec;
  text-align: left;
  vertical-align: top;
  white-space: nowrap;
}

.chat-md >>> td {
  white-space: normal;
  min-width: 5em;
}

.chat-md >>> th,
.chat-md >>> td {
  overflow-wrap: normal;
  word-break: normal;
}

.chat-md >>> td code {
  white-space: nowrap;
}

.chat-md >>> th {
  border-bottom: 1px solid #d9d9d9;
  background: none;
  font-weight: 600;
}

.chat-md >>> a.gli-link {
  color: #0b57d0;
  text-decoration: none;
  border-bottom: 1px solid rgba(11, 87, 208, .35);
  cursor: pointer;
}

.chat-md >>> a.gli-link:hover {
  border-bottom-color: #0b57d0;
}
</style>
